import React, { useState, useEffect, useRef } from 'react';
import {
  InboxProps,
  InboxItem,
  UIDSLComponent,
  UIDSLComponentType,
  UIDSLComponentProps,
} from '@dreamai/types';
import { Form } from 'react-bootstrap';
import { useInbox } from '../../../../providers';
import { useAgent } from '../../../../contexts/AgentContext';
import './inbox-component.scss';
import { ReactComponent as CheckCircleIcon } from '../../../../icons/check-circle.svg';
import { ReactComponent as ChevronDownIcon } from '../../../../icons/down-caret.svg';
import { ReactComponent as ChevronUpIcon } from '../../../../icons/up-caret.svg';
import { ReactComponent as EmailIcon } from '../../../../icons/dreambox/email.svg';
import { ReactComponent as ReminderIcon } from '../../../../icons/dreambox/reminder.svg';
import { ReactComponent as ApprovalRequiredIcon } from '../../../../icons/dreambox/approval-required.svg';
import { ReactComponent as InformationIcon } from '../../../../icons/dreambox/information.svg';
import { ReactComponent as EmailInboxIcon } from '../../../../icons/dreambox/email-inbox.svg';
import { ReactComponent as ErrorSquareIcon } from '../../../../icons/dreambox/error-square.svg';
import DreamboxIcon from '../../../../icons/dreambox.png';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';
import { UIUpdate } from '../UIComponentRenderer';
import { generateColorVariants } from '../../sidebar/threadUtils';
import ComponentLinkedThread from './ComponentLinkedThread';

// Interface for managing inbox view state
interface InboxViewState {
  mode: 'list' | 'component';
  selectedItemId?: string;
}

// Component props interface that extends InboxProps
interface InboxComponentProps extends InboxProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

const InboxComponent: React.FC<InboxComponentProps> = ({
  for: scope,
  agentId,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const {
    getInbox,
    refreshInbox,
    dismissItem,
    markAsRead,
    changeUrgency,
    isLoading,
  } = useInbox();

  const { agents } = useAgent();

  // Create a map of agent IDs to agent names for easy lookup
  const agentNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    agents.forEach((agent) => {
      map[agent.id] = agent.name || agent.agent_type || 'Unknown Agent';
    });
    return map;
  }, [agents]);

  // State for managing view mode (list vs component detail)
  const [viewState, setViewState] = useState<InboxViewState>({ mode: 'list' });

  // State for managing filter mode (priority vs agent) - only used when agentId is 'all' or undefined
  const [filterMode, setFilterMode] = useState<'priority' | 'agent'>(
    'priority',
  );

  // State for managing collapsed agent groups (agentId -> boolean)
  const [collapsedGroups, setCollapsedGroups] = useState<
    Record<string, boolean>
  >({});

  // State for managing show completed toggle
  const [showCompleted, setShowCompleted] = useState<boolean>(false);

  // State for managing swipe gestures on mobile
  const [swipeState, setSwipeState] = useState<{
    itemId: string | null;
    startX: number;
    currentX: number;
    isDragging: boolean;
  }>({
    itemId: null,
    startX: 0,
    currentX: 0,
    isDragging: false,
  });

  // Handle clicking on an inbox item to view details
  const handleItemClick = (itemId: string) => {
    setViewState({
      mode: 'component',
      selectedItemId: itemId,
    });
  };

  // Handle returning to the inbox list
  const handleBackToList = () => {
    setViewState({ mode: 'list' });
  };

  // Get inbox data based on scope
  const inboxKey = scope === 'company' ? 'all' : agentId;
  const inbox = getInbox(inboxKey);

  // Refresh inbox on mount
  useEffect(() => {
    refreshInbox(inboxKey === 'all' ? undefined : inboxKey);
  }, [refreshInbox, inboxKey]);

  // Filter items by completion status and urgency
  const activeItems = inbox.items.filter((item) => !item.dismissedAt);
  const completedItems = inbox.items.filter((item) => item.dismissedAt);
  const urgentItems = activeItems.filter((item) => item.isUrgent);
  const regularItems = activeItems.filter((item) => !item.isUrgent);

  // Calculate visible item count based on current filter state
  const visibleItemCount = showCompleted
    ? activeItems.length + completedItems.length // Show both active and completed
    : activeItems.length; // Show only active items (regardless of priority/agent filter mode)

  // Handle item actions
  const handleDismiss = async (itemId: string) => {
    await dismissItem(itemId);
  };

  const handleMarkAsRead = async (itemId: string) => {
    await markAsRead(itemId);
  };

  const handleToggleUrgency = async (itemId: string, isUrgent: boolean) => {
    await changeUrgency(itemId, isUrgent);
  };

  // Handle toggling between priority and agent filter modes
  const handleToggleFilterMode = () => {
    setFilterMode(filterMode === 'priority' ? 'agent' : 'priority');
  };

  // Handle toggling group collapse state
  const handleToggleGroupCollapse = (agentId: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [agentId]: !prev[agentId],
    }));
  };

  // Swipe gesture handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, itemId: string) => {
    // Only enable swipe on mobile and for non-completed items
    if (window.innerWidth > 768) return;

    const item = inbox.items.find((i) => i.id === itemId);
    if (item?.dismissedAt) return; // Don't allow swipe on completed items

    const touch = e.touches[0];
    setSwipeState({
      itemId,
      startX: touch.clientX,
      currentX: touch.clientX,
      isDragging: true,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipeState.isDragging || !swipeState.itemId) return;

    const touch = e.touches[0];
    setSwipeState((prev) => ({
      ...prev,
      currentX: touch.clientX,
    }));
  };

  const handleTouchEnd = async () => {
    if (!swipeState.isDragging || !swipeState.itemId) return;

    const deltaX = swipeState.currentX - swipeState.startX;
    const swipeThreshold = 100; // Minimum distance to trigger complete

    // If swiped left enough, mark as completed
    if (deltaX < -swipeThreshold) {
      await handleDismiss(swipeState.itemId);
    }

    // Reset swipe state
    setSwipeState({
      itemId: null,
      startX: 0,
      currentX: 0,
      isDragging: false,
    });
  };

  // Format date like EmailListTableView
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'No date';
    try {
      const emailDate = new Date(dateString);

      // For today's emails, show relative time
      if (isToday(emailDate)) {
        return formatDistanceToNow(emailDate, { addSuffix: true });
      }

      // For yesterday's emails
      if (isYesterday(emailDate)) {
        return `Yesterday ${format(emailDate, 'HH:mm')}`;
      }

      // For this week's emails
      if (isThisWeek(emailDate)) {
        return format(emailDate, 'EEE HH:mm'); // e.g., "Mon 14:30"
      }

      // For older emails, show formatted date/time: "09:01:33, Thu, 3 Jul"
      return format(emailDate, 'HH:mm:ss, EEE, d MMM');
    } catch {
      return dateString;
    }
  };

  const getIconFor = (iconName: string) => {
    switch (iconName) {
      case 'email':
        return <EmailIcon />;
      case 'reminder':
        return <ReminderIcon />;
      case 'approval-required':
        return <ApprovalRequiredIcon />;
      case 'information':
        return <InformationIcon />;
      default:
        return <InformationIcon />;
    }
  };

  // Render individual inbox item
  const renderInboxItem = (item: InboxItem) => {
    // Get agent info for the item
    const agent = agents.find((a) => a.id === item.agentId);
    const agentName = agentNameMap[item.agentId || ''] || 'Unknown Agent';
    const isCompleted = !!item.dismissedAt;

    // Generate color variants for the agent
    const colorVariants = agent?.predominant_colour
      ? generateColorVariants(agent.predominant_colour)
      : { base: '#97D5FC', darker: '#4A90C2', lighter: '#E3F2FD' };

    // Calculate swipe transform for visual feedback
    const isCurrentlyBeingSwiped =
      swipeState.itemId === item.id && swipeState.isDragging;
    const swipeDistance = isCurrentlyBeingSwiped
      ? swipeState.currentX - swipeState.startX
      : 0;
    const clampedSwipeDistance = Math.min(0, Math.max(swipeDistance, -150)); // Clamp between -150px and 0
    const swipeOpacity = Math.max(
      0.3,
      1 - (Math.abs(clampedSwipeDistance) / 150) * 0.7,
    ); // Fade out as swiped

    return (
      <div
        key={item.id}
        className={`inbox-item ${item.isRead ? 'read' : 'unread'} ${item.isUrgent ? 'urgent' : ''} ${isCurrentlyBeingSwiped ? 'swiping' : ''}`}
        style={{
          transform: isCurrentlyBeingSwiped
            ? `translateX(${clampedSwipeDistance}px)`
            : undefined,
          opacity: isCurrentlyBeingSwiped ? swipeOpacity : undefined,
          transition: isCurrentlyBeingSwiped
            ? 'none'
            : 'transform 0.3s ease, opacity 0.3s ease',
        }}
        onTouchStart={(e) => handleTouchStart(e, item.id)}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <span className='inbox-item-category urgent mobile-only'>
          <span className='urgent-marker-mb '>!</span>
        </span>

        <div
          className='item-header-left'
          onClick={() => handleItemClick(item.id)}
        >
          <div className='inbox-item-commentary'>
            <span className='inbox-item-commentary-icon'>
              {getIconFor(item.suggestedIcon)}
            </span>
            {item.whyThisIsInInbox}
          </div>
          <div className='inbox-item-component-full'>
            {(() => {
              // Create UIDSLComponent from inbox item
              const uiComponent: UIDSLComponent = {
                type: item.componentType as UIDSLComponentType,
                id: item.id,
                props: {
                  ...item.componentProps,
                  componentId: item.id,
                  isInboxView: true,
                } as unknown as UIDSLComponentProps,
              };

              return (
                <UIUpdate
                  component={uiComponent}
                  viewId={`inbox-${item.id}`}
                  threadId={threadId || null}
                  section='inbox'
                  onUserInput={() => {}}
                  updateThreadViewVisualDescription={
                    updateThreadViewVisualDescription
                  }
                  isInboxView={true}
                  onInboxViewComponentViewClick={(componentId) => {
                    // Handle inbox view component click - expand to show full component
                    handleItemClick(item.id);
                  }}
                />
              );
            })()}
          </div>
        </div>

        <div className='actions'>
          {/* Show agent tag in priority mode (not for completed items) */}
          {filterMode === 'priority' && !isCompleted && (
            <div
              className='agent-tag'
              style={{ backgroundColor: colorVariants.base }}
            >
              <div
                className='agent-dreamie-icon'
                style={{ backgroundColor: colorVariants.darker }}
              >
                <img src={agent?.image_url || DreamboxIcon} alt={agentName} />
              </div>
              <span className='agent-name'>{agentName}</span>
            </div>
          )}

          {/* Show completed tag for completed items */}
          {isCompleted ? (
            <>
              <ComponentLinkedThread
                componentName={item.componentType}
                match={item.componentProps}
                agentId={item.agentId}
                className='agent-thread-button'
              >
                <></>
              </ComponentLinkedThread>
              <div className='inbox-item-meta'>
                <span className='inbox-item-category dk-only completed'>
                  Completed
                  <CheckCircleIcon />
                </span>
              </div>
            </>
          ) : (
            /* Show urgent/mark urgent buttons for active items */
            <>
              {item.isUrgent ? (
                <div className='inbox-item-meta dk-only'>
                  <span className='inbox-item-category urgent dk-only'>
                    Urgent
                    <span
                      className='urgent-remove-btn'
                      onClick={() => handleToggleUrgency(item.id, false)}
                      title='Mark as Not Urgent'
                    >
                      ×
                    </span>
                  </span>
                </div>
              ) : (
                <button
                  className='inbox-action-btn dk-only'
                  onClick={() => handleToggleUrgency(item.id, true)}
                  title='Mark as Urgent'
                >
                  Mark Urgent
                </button>
              )}
              <ComponentLinkedThread
                componentName={item.componentType}
                match={item.componentProps}
                agentId={item.agentId}
                className='agent-thread-button'
              >
                <></>
              </ComponentLinkedThread>
              <div className='complete-button-wrapper dk-only'>
                <button
                  className='inbox-action-btn complete'
                  onClick={() => handleDismiss(item.id)}
                  title='Dismiss'
                >
                  <CheckCircleIcon />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (isLoading(inboxKey)) {
    return (
      <div className='inbox-component loading'>
        <div className='dreambox-header'>
          <div className='title-wrapper'>
            <img src={DreamboxIcon} />
            <div className='mobile-only'>
              <EmailInboxIcon />
            </div>
            <h1>Dreambox</h1>
          </div>
        </div>
        <div className='inbox-content'>
          <div className='inbox-main'>
            <div className='items-header'>
              <div className='item-count'>
                <p>Action Items</p>
                <span className='loading-placeholder'>—</span>
              </div>
            </div>
            <div className='inbox-items'>
              <div className='group'>
                <div className='group-items single-group'>
                  {/* Loading placeholder items */}
                  {[1, 2, 3].map((index) => (
                    <div key={index} className='inbox-item loading-placeholder'>
                      <div className='item-header-left'>
                        <div className='inbox-item-commentary'>
                          <span className='inbox-item-commentary-icon loading-placeholder'>
                            <div className='placeholder-icon'></div>
                          </span>
                          <div className='placeholder-text'></div>
                        </div>
                        <div className='inbox-item-component-full'>
                          <div className='placeholder-component'></div>
                        </div>
                      </div>
                      <div className='actions'>
                        <div className='placeholder-button'></div>
                        <div className='placeholder-button'></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (inbox.error) {
    return (
      <div className='inbox-component error'>
        <div className='dreambox-header'>
          <div className='title-wrapper'>
            <img src={DreamboxIcon} />
            <div className='mobile-only'>
              <EmailInboxIcon />
            </div>
            <h1>Dreambox</h1>
          </div>
        </div>
        <div className='inbox-error'>
          <div className='error-icon'>
            <ErrorSquareIcon />
          </div>
          <div className='error-message'>{inbox.error.message}</div>
          <button
            className='inbox-action-btn'
            onClick={() =>
              refreshInbox(inboxKey === 'all' ? undefined : inboxKey)
            }
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render content based on current view mode
  const renderContent = () => {
    // Show detailed component view
    if (viewState.mode === 'component' && viewState.selectedItemId) {
      const selectedItem = inbox.items.find(
        (item) => item.id === viewState.selectedItemId,
      );

      if (!selectedItem) {
        return (
          <div className='inbox-error'>
            <p>Item not found</p>
            <button onClick={handleBackToList}>← Back to Dreambox</button>
          </div>
        );
      }

      return (
        <div className='inbox-detail-view'>
          <div className='inbox-detail-header'>
            <button
              className='back-to-list-button'
              onClick={handleBackToList}
              type='button'
            >
              ← Back to Dreambox
            </button>
            <ComponentLinkedThread
              componentName={selectedItem.componentType}
              match={selectedItem.componentProps}
              agentId={selectedItem.agentId}
              label='Discuss'
            >
              <></>
            </ComponentLinkedThread>
          </div>
          <div className='inbox-detail-content'>
            {(() => {
              // Create UIDSLComponent from selected inbox item
              const uiComponent: UIDSLComponent = {
                type: selectedItem.componentType as UIDSLComponentType,
                id: selectedItem.id,
                props: {
                  ...selectedItem.componentProps,
                  componentId: selectedItem.id,
                } as unknown as UIDSLComponentProps,
              };

              return (
                <UIUpdate
                  component={uiComponent}
                  viewId={`inbox-detail-${selectedItem.id}`}
                  threadId={threadId || null}
                  section='inbox'
                  onUserInput={() => {}}
                  updateThreadViewVisualDescription={
                    updateThreadViewVisualDescription
                  }
                  isInboxView={false} // Show full component, not inbox view
                  onInboxViewComponentViewClick={() => {}}
                />
              );
            })()}
          </div>
        </div>
      );
    }

    // Show inbox list view (default)
    return (
      <div className='inbox-content'>
        {/* All Items - Urgent first, then regular inbox items */}
        {inbox.items.length > 0 ? (
          <div className='inbox-main'>
            <div className='items-header'>
              <div className='item-count'>
                <p>Action Items</p>
                <span>{visibleItemCount}</span>
              </div>
              {/* Only show filter when agentId is 'all' or undefined (multi-agent view) and not showing completed */}
              {(inboxKey === 'all' || agentId === undefined) &&
                !showCompleted && (
                  <div className='item-filter'>
                    <button
                      className='item-filter-btn'
                      onClick={handleToggleFilterMode}
                    >
                      {filterMode === 'priority'
                        ? 'Show by agent'
                        : 'Show by priority'}
                    </button>
                  </div>
                )}
            </div>
            <div className='inbox-items'>
              {showCompleted ? (
                // When showing completed: Always use category mode with All actions and Completed groups
                <>
                  {/* All Actions Group - Active items only */}
                  {activeItems.length > 0 && (
                    <div className='group'>
                      <div className='group-header'>
                        <h3>All actions</h3>
                      </div>
                      <div className='group-items'>
                        {/* Render urgent items first */}
                        {urgentItems.map(renderInboxItem)}
                        {/* Then render non-urgent items */}
                        {regularItems.map(renderInboxItem)}
                      </div>
                    </div>
                  )}

                  {/* Completed Group */}
                  {completedItems.length > 0 && (
                    <div className='group'>
                      <div className='group-header'>
                        <h3>Completed</h3>
                      </div>
                      <div className='group-items'>
                        {completedItems.map(renderInboxItem)}
                      </div>
                    </div>
                  )}
                </>
              ) : filterMode === 'priority' ? (
                // Priority mode: Show urgent items first, then regular items (active only)
                <div className='group'>
                  <div className='group-items single-group'>
                    {/* Render urgent items first */}
                    {urgentItems.map(renderInboxItem)}
                    {/* Then render non-urgent items */}
                    {regularItems.map(renderInboxItem)}
                  </div>
                </div>
              ) : (
                // Agent mode: Group active items by agentId
                (() => {
                  // Group active items by agentId
                  const groupedByAgent = activeItems.reduce(
                    (groups, item) => {
                      const agentId = item.agentId || 'unknown';
                      if (!groups[agentId]) {
                        groups[agentId] = [];
                      }
                      groups[agentId].push(item);
                      return groups;
                    },
                    {} as Record<string, typeof activeItems>,
                  );

                  return Object.entries(groupedByAgent).map(
                    ([agentId, items]) => {
                      const agentName =
                        agentNameMap[agentId] || 'Unknown Agent';
                      const isCollapsed = collapsedGroups[agentId] || false;
                      return (
                        <div key={agentId} className='group'>
                          <div
                            className='group-header clickable'
                            onClick={() => handleToggleGroupCollapse(agentId)}
                          >
                            <h3>{agentName}</h3>
                            <div className='group-caret'>
                              {isCollapsed ? (
                                <ChevronDownIcon />
                              ) : (
                                <ChevronUpIcon />
                              )}
                            </div>
                          </div>
                          {!isCollapsed && (
                            <div className='group-items'>
                              {items.map(renderInboxItem)}
                            </div>
                          )}
                        </div>
                      );
                    },
                  );
                })()
              )}
            </div>
          </div>
        ) : (
          /* Empty state */
          <div className='inbox-empty'>
            <img src={DreamboxIcon} />
            <h4>Important actions will show here</h4>
            <p>
              You can add reminders, or pin things here too. Just ask any of
              your team.
            </p>
          </div>
        )}

        {inbox.notYetValidCount > -1 && (
          <div className='inbox-not-yet-due'>
            <span>Items added for the future</span>
            <span className='count'>{inbox.notYetValidCount}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='inbox-component'>
      {viewState.mode !== 'component' && (
        <div className='dreambox-header'>
          <div className='title-wrapper'>
            <img src={DreamboxIcon} />
            <div className='mobile-only'>
              <EmailInboxIcon />
            </div>
            <h1>Dreambox</h1>
          </div>
          <div className='show-completed-toggle'>
            <Form.Check
              type='switch'
              id='show-completed-switch'
              label='Show completed'
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
          </div>
        </div>
      )}
      {renderContent()}
    </div>
  );
};

export default InboxComponent;
